import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Eraser, Save, PenLine } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

/**
 * Touch/mouse signature pad backed by a plain HTML5 canvas. No external
 * library, works on tablet and desktop. On save we downscale to a base64
 * PNG data URL and post to /admin/jobs/{id}/signature.
 */
export const SignaturePadModal = ({ job, onClose, onSaved }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasStrokes = useRef(false);
  const [signedBy, setSignedBy] = useState(job.client_signature_by || job.client_name || "");
  const [busy, setBusy] = useState(false);

  // Size the canvas to its container on mount + on resize
  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = rect.width * ratio;
      canvas.height = 200 * ratio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `200px`;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = "#111619";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    hasStrokes.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasStrokes.current = false;
  };

  const save = async () => {
    if (!hasStrokes.current) {
      toast.error("Please sign in the box before saving");
      return;
    }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    setBusy(true);
    try {
      await adminApi.saveJobSignature(job.id, dataUrl, signedBy.trim() || null);
      toast.success("Signature saved to job");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="signature-pad-modal"
    >
      <div className="bg-white rounded-md shadow-xl max-w-2xl w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">
              Signature capture
            </div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              Client signature
            </h2>
            <p className="text-xs text-[#3f4b52] mt-1">
              Sign in the box below — it will appear on the Claim Form and Excess Invoice PDFs.
            </p>
          </div>
          <button onClick={onClose} className="text-[#3f4b52]" aria-label="Close" data-testid="signature-close">
            <X size={18} />
          </button>
        </div>

        <div>
          <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Signed by</label>
          <input
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
            placeholder="Client name"
            className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
            data-testid="signature-signed-by"
          />
        </div>

        <div className="mt-4 border-2 border-dashed border-[#cfd7db] rounded-md bg-[#f5f6f6] p-2">
          <canvas
            ref={canvasRef}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            style={{ touchAction: "none", background: "white", borderRadius: 4, cursor: "crosshair" }}
            data-testid="signature-canvas"
          />
          <div className="text-[11px] text-[#3f4b52] mt-1 flex items-center gap-1 justify-center">
            <PenLine size={11} /> Sign with mouse, stylus or finger
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={clear}
            data-testid="signature-clear"
            className="text-xs uppercase font-display tracking-widest text-[#3f4b52] inline-flex items-center gap-1 hover:text-[#111619]"
          >
            <Eraser size={12} /> Clear
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={busy}
              data-testid="signature-save"
              className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturePadModal;
